import { IXmlNode } from '@/app-modules/server-communications';
import { safeCast } from '@/helper';
import { GameIdentity } from '@/components/game/_domain/models';
import { IPrizeStructure } from '@/components/game/implementations/_default/_domain/models';

abstract class PrizeData {
  public prizeStructure: IPrizeStructure;
  public readonly isTwoLineGame: boolean;

  protected abstract getPrizeStructure(node: IXmlNode, gameIdentity?: GameIdentity): IPrizeStructure;

  protected parsePromoType(node: IXmlNode): string | number {
    const promoType = node.attributes.promotype;
    if (promoType === null || promoType === undefined) {
      return 0;
    }

    const promoTypeNumber = parseInt(promoType, 10);
    if (!isNaN(promoTypeNumber)) {
      return promoTypeNumber;
    }

    return promoType;
  }

  constructor(node: IXmlNode, gameIdentity?: GameIdentity) {
    this.prizeStructure = this.getPrizeStructure(node, gameIdentity);
    this.isTwoLineGame = safeCast(node.attributes.twoline, false);
  }
}

abstract class CallsData extends PrizeData {
  public readonly callNumber: number;
  public readonly callValue: number;
  public readonly colour: number;
  public readonly score: number;
  public readonly callWord: string;

  constructor(node: IXmlNode, gameIdentity?: GameIdentity) {
    super(node, gameIdentity);
    this.callNumber = safeCast(node.attributes.callno, 0);
    this.callValue = safeCast(node.attributes.call, 0);
    this.colour = safeCast(node.attributes.colour, 0);
    this.score = safeCast(node.attributes.score, 0);
    this.callWord = node.attributes.callword;
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return null;
  }
}

enum CallValueParseType {
  default,
  comma,
}

abstract class CatchUpData extends PrizeData {
  public readonly gameNo: number;
  public readonly gameStage: number;
  public readonly numberOfPlayers: number;
  public readonly isLinkedGame: boolean;
  public readonly ticketcount: number;
  public readonly callCount: number;
  public readonly callValues: string[] = [];
  public readonly pattern: string;
  public readonly patternName: string;
  public readonly promoType: string | number;
  public readonly promoMultiplier: number;
  public readonly colours: string[] = [];
  public readonly theme: number;
  public readonly subtheme: string;

  constructor(node: IXmlNode, gameIdentity?: GameIdentity, callValueParseType: CallValueParseType = CallValueParseType.default) {
    super(node, gameIdentity);
    this.gameNo = safeCast(node.attributes.gameno, 0);
    this.gameStage = safeCast(node.attributes.gamestage, 0);
    this.numberOfPlayers = safeCast(node.attributes.noplayers, 0);
    this.isLinkedGame = safeCast(node.attributes.linked, false);
    this.ticketcount = safeCast(node.attributes.notickets, 0);
    this.callCount = safeCast(node.attributes.quantity, 0);
    this.pattern = node.attributes.pattern;
    this.patternName = node.attributes.patternname;
    this.promoType = this.parsePromoType(node);
    this.promoMultiplier = safeCast(node.attributes.promomultiplier, 0);
    this.colours = [...(safeCast(node.attributes.colours, '').split(''))];
    this.theme = safeCast(node.attributes.theme, 0);
    this.subtheme = node.attributes.subtheme;

    switch (callValueParseType) {
      case CallValueParseType.comma:
        this.callValues = [...(safeCast(node.attributes.calls, '').split(','))];
        break;
      case CallValueParseType.default:
        this.callValues = [...node.attributes.calls.match(/.{1,2}/g)];
        break;
    }
  }
}

abstract class NextGameData extends PrizeData {
  public readonly gameNumber: number;
  public readonly isLinkedGame: boolean;
  public readonly promoType: string | number;
  public readonly time: number;
  public readonly pattern: string;
  public readonly patternName: string;
  public readonly theme: number;
  public readonly subtheme: string;

  constructor(node: IXmlNode, gameIdentity?: GameIdentity) {
    super(node, gameIdentity);
    this.gameNumber = safeCast(node.attributes.gameno, 0);
    this.isLinkedGame = safeCast(node.attributes.linked, false);
    this.promoType = this.parsePromoType(node);
    this.time = safeCast(node.attributes.time, 0);
    this.pattern = node.attributes.pattern;
    this.patternName = node.attributes.patternname;
    this.theme = safeCast(node.attributes.theme, 0);
    this.subtheme = node.attributes.subtheme;
  }
}

abstract class ResetData extends PrizeData {
  public readonly gameId: number;
  public readonly isLinkedGame: boolean;
  public readonly numberOfPlayers: number;
  public readonly promoType: string | number;
  public readonly promoMultiplier: number;
  public readonly theme: number;
  public readonly subtheme: string;

  constructor(node: IXmlNode, gameIdentity?: GameIdentity) {
    super(node, gameIdentity);
    this.gameId = safeCast(node.attributes.gameno, 0);
    this.isLinkedGame = safeCast(node.attributes.linked, false);
    this.numberOfPlayers = safeCast(node.attributes.noplayers, 0);
    this.promoType = this.parsePromoType(node);
    this.promoMultiplier = safeCast(node.attributes.promomultiplier, 0);
    this.theme = safeCast(node.attributes.theme, 0);
    this.subtheme = node.attributes.subtheme;
  }
}

abstract class BoxNumbersData extends PrizeData {
  public readonly numbers: number[];
  public readonly prizes: number[];
  public readonly numberPrizes: Record<string, number>;

  constructor(node: IXmlNode, gameIdentity?: GameIdentity) {
    super(node, gameIdentity);
    this.numbers = safeCast(node.attributes.numbers, '').split(',').map((n: string) => safeCast(n, 0));
    this.prizes = safeCast(node.attributes.prizes, '').split(',').map((n: string) => safeCast(n, 0));
    this.numberPrizes = Object.assign({}, ...this.numbers.map((e, i) => ({ [e.toString()]: this.prizes[i] })));
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return null;
  }
}

export {
  PrizeData,
  CallsData,
  CatchUpData,
  NextGameData,
  ResetData,
  CallValueParseType,
  BoxNumbersData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/_domain/server/prize/prize-data.ts